import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import type { BreadcrumbItem } from '../shell/Breadcrumbs';

/**
 * useBreadcrumbs — resolve the top-bar breadcrumb trail for the current path.
 *
 * Detail routes are data-driven, not path-humanized: for every `/<entity>/:id`
 * page the hook resolves the record and builds a correct, consistent parent
 * trail from its foreign keys, so a detail crumb reads like the entity's real
 * ownership chain instead of a fabricated Title-Case slug.
 *
 * Two trail shapes:
 *   - ownership — the record belongs to a business (clients, contacts,
 *     documents, invoices, invoice items, fee terms). Rooted at "Businesses",
 *     the trail walks whichever ancestors the record's FKs resolve to and ends
 *     at the record's human name. E.g. a document owned by a business reads
 *     "Businesses › 100 Black Men of America › Q3 Notice"; an invoice item
 *     reads "Businesses › … › INV-093874 › Invoice item".
 *   - list — a top-level reference/system record with no ownership parent
 *     (parent organizations, users). Rooted at the entity's own list, the trail
 *     resolves the record's name: "Parent organizations › IGNITE! Central".
 *
 * When a name (or an ancestor) can't be resolved — still loading, a failed
 * fetch, or a missing FK — the crumb falls back to the singular entity name
 * ("Document", "Person") and NEVER a humanized id. Ancestor fetches reuse the
 * detail pages' own query keys (['client', id] etc.) so an open detail page and
 * its breadcrumb share one cached request.
 *
 * Every other route (the dashboard, list pages, deep/utility routes) uses the
 * generic capitalize/un-hyphenate mapping, with a `looksLikeId` safety net that
 * renders the singular entity name for any opaque id segment.
 */

interface ClientRow { id: string; name: string | null; short_name: string | null; }
interface InvoiceRow { id: string; invoice_number: string | null; client_id: string | null; }

/**
 * A detail record — a superset of the label + foreign-key fields any detail
 * entity may carry. The hook reads only what a given entity's config extracts;
 * every field is optional so one shape serves them all.
 */
interface DetailRow {
  id?: string;
  name?: string | null;
  short_name?: string | null;
  invoice_number?: string | null;
  title?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  client_id?: string | null;
  invoice_id?: string | null;
  [k: string]: unknown;
}

/**
 * Route segment → the product's own vocabulary.
 *
 * The tables keep their inherited names (`clients`, `parent_clients`,
 * `contacts`) and so do their routes, but the product calls them Businesses,
 * Parent organizations and People — and the sidebar (App.tsx) already labels
 * them that way. Resolving the label here instead of humanizing the path
 * segment keeps a trail from reading "Clients" under a nav item that reads
 * "Businesses".
 */
const SEGMENT_LABEL: Record<string, { plural: string; singular: string }> = {
  clients: { plural: 'Businesses', singular: 'Business' },
  'parent-clients': { plural: 'Parent organizations', singular: 'Parent organization' },
  contacts: { plural: 'People', singular: 'Person' },
};

/** The root of every ownership trail — the business register. */
const BUSINESSES_ROOT: BreadcrumbItem = { label: SEGMENT_LABEL.clients.plural, to: '/clients' };

function humanizeSlug(seg: string): string {
  return seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** The list-page label for a path segment: vocabulary first, humanized otherwise. */
function segmentLabel(seg: string): string {
  return SEGMENT_LABEL[seg]?.plural ?? humanizeSlug(seg);
}

/**
 * An opaque record id (a UUID or a `demo-…` seed slug) — as opposed to a real
 * path word. Humanizing one produces garbage like "Demo Doc Piedmont 1 1", so a
 * detail crumb on such a segment falls back to the singular of its parent
 * entity ("Document") instead.
 */
function looksLikeId(seg: string): boolean {
  return /^demo-/i.test(seg)
    || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-/i.test(seg)
    || /^[0-9a-f]{16,}$/i.test(seg);
}

function singularEntity(entitySeg: string): string {
  return SEGMENT_LABEL[entitySeg]?.singular ?? humanizeSlug(entitySeg).replace(/s$/, '');
}

function genericCrumbs(pathname: string): BreadcrumbItem[] {
  if (pathname === '/' || pathname === '') return [{ label: 'Dashboard' }];
  const segments = pathname.split('/').filter(Boolean);
  return segments.map((seg, i) => {
    const isLast = i === segments.length - 1;
    const label = i > 0 && looksLikeId(seg) ? singularEntity(segments[i - 1]) : segmentLabel(seg);
    const to = '/' + segments.slice(0, i + 1).join('/');
    return isLast ? { label } : { label, to };
  });
}

type TrailMode = 'ownership' | 'list';

interface DetailConfig {
  /** Endpoint that returns the record. */
  path: (id: string) => string;
  /** Cache key — mirrors the detail page's own key so they share one request. */
  queryKey: (id: string) => unknown[];
  /** The human leaf label, or undefined when unresolved (→ `singular`). */
  leaf: (row: DetailRow | undefined) => string | undefined;
  /** Fallback leaf when the record/name can't be resolved. Never an id. */
  singular: string;
  mode: TrailMode;
  /** list mode: the root crumb (the entity's own list). */
  rootList?: BreadcrumbItem;
  /** ownership mode: how to read each ancestor FK off the record. */
  clientIdOf?: (row: DetailRow) => string | null | undefined;
  invoiceIdOf?: (row: DetailRow) => string | null | undefined;
}

const contactName = (r: DetailRow): string | undefined =>
  [r.first_name, r.last_name].filter(Boolean).join(' ') || r.title || undefined;

const userName = (r: DetailRow): string | undefined =>
  [r.first_name, r.last_name].filter(Boolean).join(' ') || r.email || undefined;

/**
 * Per-entity detail-route resolvers, keyed by the first path segment. The one
 * core ownership entity (clients) is handled inline below — everything else
 * that has a resolvable name/parent lives here.
 */
const DETAIL_CONFIG: Record<string, DetailConfig> = {
  // ── ownership-rooted (business → record) ──
  documents: {
    path: (id) => `/api/documents/${id}`,
    queryKey: (id) => ['documents', id],
    leaf: (r) => r?.name || undefined,
    singular: 'Document',
    mode: 'ownership',
    clientIdOf: (r) => r.client_id,
  },
  invoices: {
    path: (id) => `/api/invoices/${id}`,
    queryKey: (id) => ['invoices', id],
    leaf: (r) => r?.invoice_number || undefined,
    singular: 'Invoice',
    mode: 'ownership',
    clientIdOf: (r) => r.client_id,
  },
  'invoice-items': {
    path: (id) => `/api/invoice-items/${id}`,
    queryKey: (id) => ['invoice-items', id],
    leaf: () => undefined,
    singular: 'Invoice item',
    mode: 'ownership',
    invoiceIdOf: (r) => r.invoice_id,
    clientIdOf: (r) => r.client_id,
  },
  contacts: {
    path: (id) => `/api/contacts/${id}`,
    queryKey: (id) => ['contacts', id],
    leaf: (r) => (r ? contactName(r) : undefined),
    singular: SEGMENT_LABEL.contacts.singular,
    mode: 'ownership',
    clientIdOf: (r) => r.client_id,
  },
  'invoice-terms': {
    path: (id) => `/api/invoice-terms/${id}`,
    queryKey: (id) => ['invoice_term', id],
    leaf: (r) => r?.name || undefined,
    singular: 'Fee terms',
    mode: 'ownership',
    // A term is usually business-scoped; a NULL client is an org-default term
    // (fee waterfall) — it still resolves its own name at the leaf.
    clientIdOf: (r) => r.client_id,
  },

  // ── list-rooted (top-level reference / system records) ──
  'parent-clients': {
    path: (id) => `/api/parent-clients/${id}`,
    queryKey: (id) => ['parent-clients', id],
    leaf: (r) => r?.name || r?.short_name || undefined,
    singular: SEGMENT_LABEL['parent-clients'].singular,
    mode: 'list',
    rootList: { label: SEGMENT_LABEL['parent-clients'].plural, to: '/parent-clients' },
  },
  users: {
    path: (id) => `/api/users/${id}`,
    queryKey: (id) => ['users', id],
    leaf: (r) => (r ? userName(r) : undefined),
    singular: 'User',
    mode: 'list',
    rootList: { label: 'Users', to: '/users' },
  },
};

/**
 * Second path segments that are NOT record ids — create wizards and static
 * sub-routes — so `/documents/review-queue` and `/clients/new` are never
 * mistaken for a detail record and instead use the generic mapping.
 */
const NON_ID_SEGMENTS = new Set(['new', 'review-queue']);

export function useBreadcrumbs(pathname: string): BreadcrumbItem[] {
  const segments = pathname.split('/').filter(Boolean);
  const entity = segments[0];
  const idSeg = segments[1];

  // A detail route is exactly `/<entity>/<id>` where the id segment is a real
  // record id — not a create wizard or a static sub-route. Any other shape
  // (deeper paths, list pages, the dashboard) uses the generic mapping.
  const isDetailRoute = segments.length === 2 && !!idSeg && !NON_ID_SEGMENTS.has(idSeg);
  const id = isDetailRoute ? idSeg : '';

  const isClientDetail = isDetailRoute && entity === 'clients';
  const config = isDetailRoute && !isClientDetail ? DETAIL_CONFIG[entity] : undefined;
  const isConfigDetail = !!config;

  // Hooks must run unconditionally every render; `enabled` gates the fetches so
  // only the chain the current route needs actually requests.

  // The record at a config-driven detail route (document, invoice, contact, …).
  const primaryQ = useQuery<DetailRow>({
    queryKey: config ? config.queryKey(id) : ['breadcrumb-primary', 'disabled'],
    queryFn: ({ signal }) => api.get<DetailRow>(config!.path(id), signal),
    enabled: isConfigDetail,
    staleTime: 60_000,
    retry: false,
  });
  const primary = isConfigDetail ? primaryQ.data : undefined;

  // Invoice ancestor — only invoice items chain through an invoice.
  const invoiceId = config?.invoiceIdOf && primary ? (config.invoiceIdOf(primary) ?? undefined) : undefined;
  const invoiceQ = useQuery<InvoiceRow>({
    queryKey: ['invoice', invoiceId],
    queryFn: ({ signal }) => api.get<InvoiceRow>(`/api/invoices/${invoiceId}`, signal),
    enabled: !!invoiceId,
    staleTime: 60_000,
    retry: false,
  });
  const invoice = invoiceQ.data;

  // Business — the client detail itself, a config record's direct client FK, or
  // its invoice's client.
  const configClientId = config?.clientIdOf && primary ? (config.clientIdOf(primary) ?? undefined) : undefined;
  const clientId = isClientDetail ? id : (configClientId ?? invoice?.client_id ?? undefined);
  const clientQ = useQuery<ClientRow>({
    queryKey: ['client', clientId],
    queryFn: ({ signal }) => api.get<ClientRow>(`/api/clients/${clientId}`, signal),
    enabled: !!clientId,
    staleTime: 60_000,
    retry: false,
  });
  const client = clientQ.data;

  const clientLabel = (c: ClientRow) => c.name || c.short_name || SEGMENT_LABEL.clients.singular;
  const invoiceLabel = (inv: InvoiceRow) => inv.invoice_number || 'Invoice';

  // ── core ownership entity ──
  if (isClientDetail) {
    return [
      BUSINESSES_ROOT,
      { label: client ? clientLabel(client) : singularEntity('clients') },
    ];
  }

  // ── config-driven detail entities ──
  if (config) {
    const leafLabel = config.leaf(primary) || config.singular;

    if (config.mode === 'list') {
      const crumbs: BreadcrumbItem[] = [];
      if (config.rootList) crumbs.push(config.rootList);
      crumbs.push({ label: leafLabel });
      return crumbs;
    }

    // ownership mode — root at Businesses and walk whichever ancestors resolved.
    const crumbs: BreadcrumbItem[] = [BUSINESSES_ROOT];
    if (client) crumbs.push({ label: clientLabel(client), to: `/clients/${client.id}` });
    if (invoice) crumbs.push({ label: invoiceLabel(invoice), to: `/invoices/${invoice.id}` });
    crumbs.push({ label: leafLabel });
    return crumbs;
  }

  return genericCrumbs(pathname);
}
